/**
 * Dermowise Notification Setup Component
 * Handles push notification registration and management
 */

import React, { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import notificationManager from '../utils/notificationManager';

const NotificationSetup = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    initializeNotifications();
  }, []);

  const initializeNotifications = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if notifications are supported
      if (!notificationManager.isSupported) {
        setIsSupported(false);
        return;
      }

      setIsSupported(true);

      // Initialize notification manager
      const initialized = await notificationManager.initialize();
      
      // Get current permission status
      const currentPermission = notificationManager.getPermissionStatus();
      setPermission(currentPermission);

      // Check if already subscribed
      const subscription = notificationManager.getSubscription();
      setIsSubscribed(!!subscription);

      if (initialized) {
        setSuccess('نوتیفیکیشن‌ها از قبل فعال هستند!');
      }

    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      setError('خطا در راه‌اندازی نوتیفیکیشن‌ها');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      // Request permission
      const permissionGranted = await notificationManager.requestPermission();
      
      if (!permissionGranted) {
        setError('اجازه نوتیفیکیشن رد شد. لطفاً در تنظیمات مرورگر اجازه دهید.');
        return;
      }

      // Subscribe to push notifications
      await notificationManager.subscribe();
      
      setIsSubscribed(true);
      setPermission('granted');
      setSuccess('نوتیفیکیشن‌ها با موفقیت فعال شدند! 🎉');

      // Send test notification
      setTimeout(async () => {
        try {
          await notificationManager.sendTestNotification();
        } catch (testError) {
          console.error('Test notification failed:', testError);
        }
      }, 1000);

    } catch (error) {
      console.error('Failed to enable notifications:', error);
      setError('خطا در فعال‌سازی نوتیفیکیشن‌ها: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      await notificationManager.unsubscribe();
      
      setIsSubscribed(false);
      setSuccess('نوتیفیکیشن‌ها غیرفعال شدند.');

    } catch (error) {
      console.error('Failed to disable notifications:', error);
      setError('خطا در غیرفعال‌سازی نوتیفیکیشن‌ها: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Debug notification status
      console.log('🔔 Debug notification status:');
      console.log('- Notification.permission:', Notification.permission);
      console.log('- Service Worker supported:', 'serviceWorker' in navigator);
      console.log('- Push Manager supported:', 'PushManager' in window);
      
      if (navigator.serviceWorker) {
        const registration = await navigator.serviceWorker.getRegistration();
        console.log('- Service Worker registration:', registration);
        
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          console.log('- Push subscription:', subscription);
        }
      }

      // First, try to show a local notification for testing
      if (Notification.permission === 'granted') {
        console.log('🔔 Showing local test notification...');
        const localNotification = new Notification('درمووایز - تست نوتیفیکیشن', {
          body: 'این یک نوتیفیکیشن تست محلی است! 🎉',
          icon: '/icons/notification.png',
          badge: '/icons/badge.png',
          tag: 'test-notification',
          requireInteraction: false,
          silent: false
        });
        
        localNotification.onclick = () => {
          console.log('🔔 Local notification clicked');
          localNotification.close();
        };
        
        setSuccess('نوتیفیکیشن تست محلی نمایش داده شد! 📱');
      } else {
        // Fallback to worker notification
        await notificationManager.sendTestNotification();
        setSuccess('نوتیفیکیشن تست ارسال شد! 📱');
      }

    } catch (error) {
      console.error('Failed to send test notification:', error);
      setError('خطا در ارسال نوتیفیکیشن تست: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <div>
            <h3 className="font-medium text-yellow-800">نوتیفیکیشن پشتیبانی نمی‌شود</h3>
            <p className="text-sm text-yellow-600 mt-1">
              مرورگر شما از نوتیفیکیشن‌های Push پشتیبانی نمی‌کند.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-5 h-5 text-pink-600" />
        <h2 className="text-lg font-bold text-gray-800">تنظیمات نوتیفیکیشن</h2>
      </div>

      {/* Status Display */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          {isSubscribed ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <XCircle className="w-4 h-4 text-red-600" />
          )}
          <span className={`font-medium text-sm ${isSubscribed ? 'text-green-600' : 'text-red-600'}`}>
            {isSubscribed ? 'نوتیفیکیشن‌ها فعال' : 'نوتیفیکیشن‌ها غیرفعال'}
          </span>
        </div>
        
        <div className="text-xs text-gray-600">
          وضعیت اجازه: 
          <span className={`ml-1 font-medium ${
            permission === 'granted' ? 'text-green-600' : 
            permission === 'denied' ? 'text-red-600' : 'text-yellow-600'
          }`}>
            {permission === 'granted' ? 'مجاز' : 
             permission === 'denied' ? 'رد شده' : 'درخواست نشده'}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {!isSubscribed ? (
          <button
            onClick={handleEnableNotifications}
            disabled={isLoading || permission === 'denied'}
            className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-400 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {isLoading ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                در حال فعال‌سازی...
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                فعال‌سازی نوتیفیکیشن‌ها
              </>
            )}
          </button>
        ) : (
          <div className="space-y-2">
            <button
              onClick={handleTestNotification}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  در حال ارسال...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  ارسال نوتیفیکیشن تست
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                console.log('🔔 Manual notification test...');
                if (Notification.permission === 'granted') {
                  try {
                    const notification = new Notification('تست دستی', {
                      body: 'این یک تست دستی است!',
                      icon: '/icons/notification.png',
                      badge: '/icons/badge.png',
                      tag: 'manual-test',
                      requireInteraction: false,
                      silent: false
                    });
                    
                    notification.onclick = () => {
                      console.log('🔔 Manual notification clicked');
                      notification.close();
                    };
                    
                    notification.onshow = () => {
                      console.log('🔔 Manual notification shown');
                    };
                    
                    notification.onerror = (error) => {
                      console.error('🔔 Manual notification error:', error);
                    };
                    
                    console.log('🔔 Manual notification created:', notification);
                  } catch (error) {
                    console.error('🔔 Failed to create manual notification:', error);
                    alert('خطا در ایجاد نوتیفیکیشن: ' + error.message);
                  }
                } else {
                  alert('لطفاً ابتدا اجازه نوتیفیکیشن را بدهید');
                }
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              تست دستی
            </button>
            
            <button
              onClick={async () => {
                console.log('🔔 Service Worker notification test...');
                try {
                  const registration = await navigator.serviceWorker.getRegistration();
                  if (registration) {
                    await registration.showNotification('تست Service Worker', {
                      body: 'این نوتیفیکیشن از Service Worker ارسال شده است!',
                      icon: '/icons/notification.png',
                      badge: '/icons/badge.png',
                      tag: 'sw-test',
                      requireInteraction: false,
                      silent: false
                    });
                    console.log('🔔 Service Worker notification sent');
                  } else {
                    console.error('🔔 No service worker registration found');
                  }
                } catch (error) {
                  console.error('🔔 Service Worker notification error:', error);
                }
              }}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              تست Service Worker
            </button>
            
            <button
              onClick={() => {
                console.log('🔔 Browser notification settings check...');
                console.log('- Notification.permission:', Notification.permission);
                console.log('- navigator.permissions:', navigator.permissions);
                console.log('- window.Notification:', window.Notification);
                console.log('- document.visibilityState:', document.visibilityState);
                console.log('- document.hidden:', document.hidden);
                
                // Check if browser is in focus
                if (document.hidden) {
                  console.log('⚠️ Browser is not in focus - notifications might not show');
                }
                
                // Check if notifications are blocked by browser
                if (Notification.permission === 'denied') {
                  console.log('❌ Notifications are blocked by browser');
                  alert('نوتیفیکیشن‌ها توسط مرورگر مسدود شده‌اند. لطفاً در تنظیمات مرورگر آن‌ها را فعال کنید.');
                } else if (Notification.permission === 'granted') {
                  console.log('✅ Notifications are allowed');
                  alert('نوتیفیکیشن‌ها مجاز هستند. اگر نمایش داده نمی‌شوند، ممکن است مرورگر در حالت Do Not Disturb باشد.');
                } else {
                  console.log('⚠️ Notification permission not requested yet');
                }
              }}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              بررسی تنظیمات
            </button>
            
            <button
              onClick={() => {
                console.log('🔔 macOS Notification Test...');
                
                // Test 1: Simple notification
                try {
                  const notif = new Notification('تست macOS', {
                    body: 'این تست مخصوص macOS است',
                    icon: '/icons/notification.png',
                    tag: 'macos-test',
                    requireInteraction: true,
                    silent: false
                  });
                  
                  notif.onshow = () => {
                    console.log('✅ نوتیفیکیشن macOS نمایش داده شد');
                    alert('✅ نوتیفیکیشن نمایش داده شد!');
                  };
                  
                  notif.onerror = (e) => {
                    console.error('❌ خطا در نوتیفیکیشن macOS:', e);
                    alert('❌ خطا در نوتیفیکیشن: ' + e.message);
                  };
                  
                  notif.onclick = () => {
                    console.log('🔔 نوتیفیکیشن macOS کلیک شد');
                    notif.close();
                  };
                  
                  console.log('✅ نوتیفیکیشن macOS ایجاد شد');
                  
                } catch (error) {
                  console.error('❌ خطا در ایجاد نوتیفیکیشن macOS:', error);
                  alert('❌ خطا در ایجاد نوتیفیکیشن: ' + error.message);
                }
              }}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              تست macOS
            </button>
            
            <button
              onClick={async () => {
                console.log('🔔 PWA Notification Test...');
                
                try {
                  const registration = await navigator.serviceWorker.getRegistration();
                  if (registration) {
                    await registration.showNotification('تست PWA', {
                      body: 'این نوتیفیکیشن از PWA Service Worker ارسال شده است!',
                      icon: '/icons/notification.png',
                      badge: '/icons/badge.png',
                      tag: 'pwa-test',
                      requireInteraction: true,
                      silent: false,
                      actions: [
                        {
                          action: 'open',
                          title: 'باز کردن',
                          icon: '/icons/open.png'
                        },
                        {
                          action: 'dismiss',
                          title: 'بستن',
                          icon: '/icons/close.png'
                        }
                      ]
                    });
                    console.log('✅ PWA نوتیفیکیشن ارسال شد');
                    alert('✅ PWA نوتیفیکیشن ارسال شد!');
                  } else {
                    console.error('❌ Service Worker registration not found');
                    alert('❌ Service Worker فعال نیست');
                  }
                } catch (error) {
                  console.error('❌ خطا در PWA نوتیفیکیشن:', error);
                  alert('❌ خطا در PWA نوتیفیکیشن: ' + error.message);
                }
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              تست PWA
            </button>
            
            <button
              onClick={async () => {
                console.log('🔔 System Notification Test...');
                
                try {
                  // Request permission first
                  const permission = await Notification.requestPermission();
                  console.log('📱 Permission:', permission);
                  
                  if (permission === 'granted') {
                    // Get service worker registration
                    const registration = await navigator.serviceWorker.ready;
                    
                    // Show notification through service worker (PWA style)
                    await registration.showNotification('🎉 درمووایز', {
                      body: 'به درمووایز خوش آمدید! این یک نوتیفیکیشن سیستمی است.',
                      icon: '/logo.svg',
                      badge: '/logo.svg',
                      tag: 'test-notification',
                      requireInteraction: true,
                      actions: [
                        {
                          action: 'open',
                          title: 'باز کردن'
                        },
                        {
                          action: 'close',
                          title: 'بستن'
                        }
                      ],
                      vibrate: [200, 100, 200],
                      data: {
                        url: '/',
                        timestamp: Date.now()
                      }
                    });
                    
                    console.log('✅ System notification sent successfully');
                    alert('✅ نوتیفیکیشن ارسال شد! اگر نمایش داده نشد، تنظیمات سیستم را بررسی کنید.');
                  } else {
                    alert('❌ اجازه نوتیفیکیشن داده نشده است');
                  }
                } catch (error) {
                  console.error('❌ System notification error:', error);
                  alert('❌ خطا در ارسال نوتیفیکیشن: ' + error.message);
                }
              }}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              🔔 تست نوتیفیکیشن سیستمی
            </button>
            
            <button
              onClick={async () => {
                console.log('🎉 Welcome Notification Test (10 seconds)...');
                
                try {
                  const { scheduleWelcomeNotification } = await import('../utils/welcomeNotification');
                  await scheduleWelcomeNotification('کاربر عزیز');
                  alert('✅ نوتیفیکیشن خوش‌آمدگویی 10 ثانیه دیگر نمایش داده می‌شود!');
                } catch (error) {
                  console.error('❌ Welcome notification error:', error);
                  alert('❌ خطا: ' + error.message);
                }
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              🎉 تست خوش‌آمدگویی (10 ثانیه)
            </button>
            
            <button
              onClick={async () => {
                console.log('🔥 SIMPLE TEST - Direct Notification');
                
                // تست خیلی ساده بدون هیچ چک اضافی
                try {
                  const perm = await Notification.requestPermission();
                  console.log('Permission:', perm);
                  
                  if (perm === 'granted') {
                    // تست 1: مستقیم
                    new Notification('تست ساده', {
                      body: 'این باید نمایش داده شود!',
                      requireInteraction: true
                    });
                    
                    alert('✅ نوتیف ارسال شد!\n\nاگر نمایش نداده شد:\n\n1. Settings سیستم را چک کنید\n2. Focus/DND خاموش باشد\n3. مرورگر را restart کنید');
                  } else {
                    alert('❌ Permission: ' + perm + '\n\nلطفاً در تنظیمات مرورگر Allow کنید');
                  }
                } catch (error) {
                  alert('❌ خطا: ' + error.message);
                }
              }}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              🔥 تست فوری (ساده)
            </button>
            
            <button
              onClick={async () => {
                console.log('🔍 Starting complete notification diagnostic...');
                let report = '🔍 تشخیص مشکل نوتیفیکیشن:\n\n';
                
                // 1. Check support
                if (!('Notification' in window)) {
                  report += '❌ مرورگر از Notifications پشتیبانی نمی‌کند\n';
                  alert(report);
                  return;
                }
                report += '✅ Notifications پشتیبانی می‌شود\n';
                
                // 2. Check permission
                report += `📱 Permission: ${Notification.permission}\n`;
                
                if (Notification.permission === 'default') {
                  report += '⚠️ در حال درخواست اجازه...\n';
                  const perm = await Notification.requestPermission();
                  report += `📱 اجازه جدید: ${perm}\n`;
                }
                
                if (Notification.permission !== 'granted') {
                  report += '❌ اجازه نوتیفیکیشن داده نشده است!\n';
                  report += '\n💡 راه‌حل:\n';
                  report += '1. Chrome Settings > Privacy > Notifications\n';
                  report += '2. Allow localhost\n';
                  alert(report);
                  return;
                }
                report += '✅ Permission granted\n\n';
                
                // 3. Test direct notification
                try {
                  new Notification('تست مستقیم', {
                    body: 'این نوتیف مستقیماً از JavaScript ارسال شد'
                  });
                  report += '✅ نوتیف مستقیم ارسال شد (باید نمایش داده شود)\n';
                } catch (error) {
                  report += `❌ خطا در نوتیف مستقیم: ${error.message}\n`;
                }
                
                // 4. Check Service Worker
                try {
                  const reg = await navigator.serviceWorker.getRegistration();
                  if (!reg) {
                    report += '❌ Service Worker ثبت نشده است\n';
                    alert(report);
                    return;
                  }
                  report += `✅ Service Worker: ${reg.active?.state || 'unknown'}\n`;
                  
                  // 5. Test SW notification
                  await reg.showNotification('تست Service Worker', {
                    body: 'این از Service Worker ارسال شد',
                    icon: '/logo.svg',
                    requireInteraction: true,
                    tag: 'diagnostic-test'
                  });
                  report += '✅ نوتیف Service Worker ارسال شد (باید نمایش داده شود)\n\n';
                  
                  report += '🎉 همه چیز درست کار می‌کند!\n';
                  report += '\n📱 اگر نوتیف نمایش نداده شد:\n';
                  report += '- macOS: System Preferences > Notifications > Chrome\n';
                  report += '- Focus Mode را خاموش کنید\n';
                  report += '- مرورگر را Restart کنید\n';
                  
                } catch (error) {
                  report += `❌ خطا در Service Worker: ${error.message}\n`;
                }
                
                console.log(report);
                alert(report);
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Bell className="w-4 h-4" />
              🔍 تشخیص مشکل (Debug)
            </button>
            
            <button
              onClick={handleDisableNotifications}
              disabled={isLoading}
              className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium py-2 px-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  در حال غیرفعال‌سازی...
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4" />
                  غیرفعال‌سازی نوتیفیکیشن‌ها
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-2">
            <XCircle className="w-3 h-3 text-red-600" />
            <span className="text-xs text-red-600">{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-green-600" />
            <span className="text-xs text-green-600">{success}</span>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-3 text-xs text-gray-500">
        <p>
          با فعال‌سازی نوتیفیکیشن‌ها، از آخرین اخبار، یادآوری‌ها و پیشنهادات درمووایز مطلع شوید.
        </p>
      </div>
    </div>
  );
};

export default NotificationSetup;
